import React, { useState } from "react";

/**
 * Sprint H — IntegrationLogo
 *
 * Tries logo.dev first (using the integration's `domain` field). If the fetch
 * fails or no domain is available, falls back to a monochrome colored circle
 * with the integration's first letter.
 *
 * logo.dev free-tier public token pattern:  https://img.logo.dev/{domain}
 *   - Their free endpoint is publicly accessible (no auth required for basic
 *     use). Set REACT_APP_LOGO_DEV_TOKEN in frontend .env if you want higher
 *     rate limits — it's appended as a query param.
 *
 * Usage:
 *    <IntegrationLogo integration={i} size={24} />
 */
export default function IntegrationLogo({ integration, size = 24, rounded = "rounded-md", className = "" }) {
    const [failed, setFailed] = useState(false);
    const rawDomain = integration?.domain;
    // Normalize to hostname only — several catalog entries store "host/path"
    // (e.g. "hubspot.com/products/service"). logo.dev needs just the host.
    const domain = rawDomain ? String(rawDomain).split("/")[0].trim() : null;
    const brand = integration?.brand_color || "#6b7280";
    const glyph = (integration?.icon_glyph || integration?.name?.[0] || "?").toUpperCase();
    const dim = { width: size, height: size };

    // Build logo.dev URL when domain is available and prior attempt didn't fail
    const token = process.env.REACT_APP_LOGO_DEV_TOKEN;
    const canUseLogoDev = !!domain && !failed;
    const url = canUseLogoDev
        ? `https://img.logo.dev/${encodeURIComponent(domain)}${token ? `?token=${token}` : ""}`
        : null;

    if (url) {
        return (
            <img
                src={url}
                alt={integration?.name || "logo"}
                onError={() => setFailed(true)}
                loading="lazy"
                data-testid={`integration-logo-${integration?.slug || integration?.id}`}
                className={`${rounded} object-contain bg-white border border-[#e8e8e3] ${className}`}
                style={dim}
            />
        );
    }

    // Fallback: monochrome colored circle with glyph.
    return (
        <div
            data-testid={`integration-glyph-${integration?.slug || integration?.id}`}
            aria-label={integration?.name}
            className={`${rounded} inline-flex items-center justify-center text-white font-semibold border border-white/10 ${className}`}
            style={{ ...dim, background: brand, fontSize: size * 0.44 }}
        >
            {glyph}
        </div>
    );
}
